import type { PublicationDetailOut } from '@/api/generated/schemas';

export type ExcludedField =
  | 'excluded_category_ids'
  | 'excluded_collection_ids'
  | 'excluded_custom_tag_ids';

export type StaticField =
  | 'static_category_ids'
  | 'static_collection_ids'
  | 'static_custom_tag_ids';

export type DynamicField =
  | 'dynamic_category_ids'
  | 'dynamic_collection_ids'
  | 'dynamic_custom_tag_ids';

export type MembersField = 'categories' | 'collections' | 'custom_tags';

export interface ExclusionFamily {
  key: 'categories' | 'collections' | 'tags';
  staticField: StaticField;
  dynamicField: DynamicField;
  excludedField: ExcludedField;
  membersField: MembersField;
}

/** The three exclusion-capable membership families of a publication. */
export const EXCLUSION_FAMILIES: ExclusionFamily[] = [
  {
    key: 'categories',
    staticField: 'static_category_ids',
    dynamicField: 'dynamic_category_ids',
    excludedField: 'excluded_category_ids',
    membersField: 'categories',
  },
  {
    key: 'collections',
    staticField: 'static_collection_ids',
    dynamicField: 'dynamic_collection_ids',
    excludedField: 'excluded_collection_ids',
    membersField: 'collections',
  },
  {
    key: 'tags',
    staticField: 'static_custom_tag_ids',
    dynamicField: 'dynamic_custom_tag_ids',
    excludedField: 'excluded_custom_tag_ids',
    membersField: 'custom_tags',
  },
];

/**
 * Ids that may legally appear in an `excluded_*_ids` PATCH: the backend only
 * accepts memberships the publication already has (static pins as they will
 * be after this request, dynamic rows, and rows that are currently excluded).
 */
export const allowedExcludedIds = (
  staticIds: number[] | null | undefined,
  dynamicIds: number[] | null | undefined,
  currentlyExcluded: number[] | null | undefined
): Set<number> =>
  new Set([
    ...(staticIds ?? []),
    ...(dynamicIds ?? []),
    ...(currentlyExcluded ?? []),
  ]);

/** Drops excluded ids that are no longer members (e.g. a pin removed in the same edit). */
export const pruneExcluded = (
  excluded: number[] | null | undefined,
  allowed: Set<number>
): number[] => (excluded ?? []).filter((id) => allowed.has(id));

/** Convenience: current allowed set for a family, given the (possibly edited) static ids. */
export const allowedForFamily = (
  record: Pick<PublicationDetailOut, DynamicField | ExcludedField>,
  family: ExclusionFamily,
  staticIds: number[] | null | undefined
): Set<number> =>
  allowedExcludedIds(
    staticIds,
    record[family.dynamicField],
    record[family.excludedField]
  );
